/**
 * AdminCohortsPage - Cohort Builder for AI tagging
 *
 * Provides filter form, stats display, preview with cursor pagination,
 * and handoff to AI tagging page.
 *
 * Uses:
 * - GET /api/admin/cohort/stats (bounded counts)
 * - GET /api/admin/cohort/preview (bounded selection)
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Eye, Loader2, ArrowRight, Copy, CheckCircle2 } from 'lucide-react';
import { AdminLayout } from './AdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import { apiFetch } from '../../lib/api';
import { GENRES, TV_GENRES } from '../../utils/constants';

// Merge movie and TV genres for the selector
const ALL_GENRES: Record<number, string> = { ...GENRES, ...TV_GENRES };

interface CohortFilters {
  media_types: ('movie' | 'tv')[];
  min_year: number | null;
  max_year: number | null;
  min_imdb_votes: number;
  max_imdb_votes: number | null;
  language_allow: string[] | null;
  language_deny: string[] | null;
  genre_ids: number[] | null;
}

interface CohortStats {
  movies_total: number;
  movies_facets_tagged: number;
  movies_facets_missing: number;
  movies_facets_unknown: number;
  movies_overlay_tagged: number;
  movies_overlay_missing: number;
  movies_overlay_unknown: number;
  tv_total: number;
  tv_facets_tagged: number;
  tv_facets_missing: number;
  tv_facets_unknown: number;
  tv_overlay_tagged: number;
  tv_overlay_missing: number;
  tv_overlay_unknown: number;
}

interface StatsResponse {
  filters: CohortFilters;
  stats: CohortStats;
}

interface PreviewItem {
  media_type: string;
  tmdb_id: number;
  title: string;
  imdb_votes: number;
  missing_fields: string[];
  original_language: string;
  year: number;
}

interface Cursor {
  imdb_votes: number;
  tmdb_id: number;
  media_type: string;
}

interface PreviewResponse {
  filters: CohortFilters & { missing_token: string };
  items: PreviewItem[];
  count: number;
  next_cursor: Cursor | null;
}

export function AdminCohortsPage() {
  const { session } = useAuth();
  const navigate = useNavigate();

  // Filter state
  const [mediaTypes, setMediaTypes] = useState<('movie' | 'tv')[]>(['movie', 'tv']);
  const [minYear, setMinYear] = useState<string>('');
  const [maxYear, setMaxYear] = useState<string>('');
  const [minImdbVotes, setMinImdbVotes] = useState(1000);
  const [maxImdbVotes, setMaxImdbVotes] = useState<string>('');
  const [selectedGenres, setSelectedGenres] = useState<number[]>([]);

  // Preview-specific state
  const [missingToken, setMissingToken] = useState<'missing_ai_facets_v1' | 'missing_ai_genre_overlay_v1'>('missing_ai_facets_v1');

  // API state
  const [statsLoading, setStatsLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [statsResult, setStatsResult] = useState<StatsResponse | null>(null);
  const [previewResult, setPreviewResult] = useState<PreviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Cursor for pagination
  const [cursorJson, setCursorJson] = useState('');

  // Copy state
  const [copiedFilters, setCopiedFilters] = useState(false);

  const buildQueryParams = (): URLSearchParams => {
    const params = new URLSearchParams();
    params.set('media_types', mediaTypes.join(','));
    params.set('min_imdb_votes', minImdbVotes.toString());
    if (minYear) params.set('min_year', minYear);
    if (maxYear) params.set('max_year', maxYear);
    if (maxImdbVotes) params.set('max_imdb_votes', maxImdbVotes);
    if (selectedGenres.length > 0) params.set('genre_ids', selectedGenres.join(','));
    return params;
  };

  const fetchStats = async () => {
    if (!session?.access_token) return;

    setStatsLoading(true);
    setError(null);

    try {
      const params = buildQueryParams();
      const response = await apiFetch<StatsResponse>(
        `/admin/cohort/stats?${params.toString()}`,
        { token: session.access_token }
      );
      setStatsResult(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch stats');
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchPreview = async () => {
    if (!session?.access_token) return;

    setPreviewLoading(true);
    setError(null);

    try {
      const params = buildQueryParams();
      params.set('missing_token', missingToken);
      params.set('limit', '100');

      // Add cursor if provided
      if (cursorJson.trim()) {
        try {
          const cursor = JSON.parse(cursorJson);
          if (cursor.imdb_votes !== undefined) params.set('cursor_imdb_votes', cursor.imdb_votes.toString());
          if (cursor.tmdb_id !== undefined) params.set('cursor_tmdb_id', cursor.tmdb_id.toString());
        } catch {
          // Invalid JSON, ignore
        }
      }

      const response = await apiFetch<PreviewResponse>(
        `/admin/cohort/preview?${params.toString()}`,
        { token: session.access_token }
      );
      setPreviewResult(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleMediaTypeToggle = (type: 'movie' | 'tv') => {
    setMediaTypes(prev => {
      if (prev.includes(type)) {
        if (prev.length === 1) return prev; // Don't allow empty
        return prev.filter(t => t !== type);
      }
      return [...prev, type];
    });
  };

  const handleGenreToggle = (genreId: number) => {
    setSelectedGenres(prev => {
      if (prev.includes(genreId)) {
        return prev.filter(id => id !== genreId);
      }
      return [...prev, genreId];
    });
  };

  const copyFiltersToClipboard = () => {
    const filters = {
      media_types: mediaTypes,
      min_year: minYear ? parseInt(minYear, 10) : null,
      max_year: maxYear ? parseInt(maxYear, 10) : null,
      min_imdb_votes: minImdbVotes,
      max_imdb_votes: maxImdbVotes ? parseInt(maxImdbVotes, 10) : null,
      genre_ids: selectedGenres.length > 0 ? selectedGenres : null,
    };
    navigator.clipboard.writeText(JSON.stringify(filters, null, 2));
    setCopiedFilters(true);
    setTimeout(() => setCopiedFilters(false), 2000);
  };

  const handoffToAiTagging = () => {
    // Navigate to AI tagging page with filter state in URL or session storage
    const filters = {
      media_types: mediaTypes,
      min_year: minYear ? parseInt(minYear, 10) : null,
      max_year: maxYear ? parseInt(maxYear, 10) : null,
      min_imdb_votes: minImdbVotes,
      max_imdb_votes: maxImdbVotes ? parseInt(maxImdbVotes, 10) : null,
      genre_ids: selectedGenres.length > 0 ? selectedGenres : null,
    };
    sessionStorage.setItem('cohortFilters', JSON.stringify(filters));
    navigate('/admin/ai-tagging');
  };

  const useCursor = () => {
    if (previewResult?.next_cursor) {
      setCursorJson(JSON.stringify(previewResult.next_cursor, null, 2));
    }
  };

  return (
    <AdminLayout
      title="Cohort Builder"
      description="Filter and preview content cohorts for AI tagging"
    >
      <div className="space-y-6">
        {/* Filter Form */}
        <div className="bg-[#1A1F26] border border-white/[0.08] rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Filters</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Media Types */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Media Types
              </label>
              <div className="flex gap-2">
                <button
                  onClick={() => handleMediaTypeToggle('movie')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    mediaTypes.includes('movie')
                      ? 'bg-teal-500/20 text-teal-400 border border-teal-500/50'
                      : 'bg-[#0F1419] text-slate-400 border border-white/[0.12] hover:border-white/[0.2]'
                  }`}
                >
                  Movies
                </button>
                <button
                  onClick={() => handleMediaTypeToggle('tv')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    mediaTypes.includes('tv')
                      ? 'bg-teal-500/20 text-teal-400 border border-teal-500/50'
                      : 'bg-[#0F1419] text-slate-400 border border-white/[0.12] hover:border-white/[0.2]'
                  }`}
                >
                  TV Shows
                </button>
              </div>
            </div>

            {/* Year Range */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Year Range
              </label>
              <div className="flex gap-2 items-center">
                <input
                  type="number"
                  value={minYear}
                  onChange={(e) => setMinYear(e.target.value)}
                  placeholder="1900"
                  className="w-24 px-3 py-2 bg-[#0F1419] border border-white/[0.12] rounded-lg text-white text-sm focus:outline-none focus:border-teal-500"
                />
                <span className="text-slate-400">-</span>
                <input
                  type="number"
                  value={maxYear}
                  onChange={(e) => setMaxYear(e.target.value)}
                  placeholder="2026"
                  className="w-24 px-3 py-2 bg-[#0F1419] border border-white/[0.12] rounded-lg text-white text-sm focus:outline-none focus:border-teal-500"
                />
              </div>
            </div>

            {/* IMDb Votes Range */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                IMDb Votes Range
              </label>
              <div className="flex gap-2 items-center">
                <input
                  type="number"
                  value={minImdbVotes}
                  onChange={(e) => setMinImdbVotes(parseInt(e.target.value, 10) || 0)}
                  placeholder="1000"
                  className="w-28 px-3 py-2 bg-[#0F1419] border border-white/[0.12] rounded-lg text-white text-sm focus:outline-none focus:border-teal-500"
                />
                <span className="text-slate-400">-</span>
                <input
                  type="number"
                  value={maxImdbVotes}
                  onChange={(e) => setMaxImdbVotes(e.target.value)}
                  placeholder="unlimited"
                  className="w-28 px-3 py-2 bg-[#0F1419] border border-white/[0.12] rounded-lg text-white text-sm focus:outline-none focus:border-teal-500"
                />
              </div>
            </div>
          </div>

          {/* Genres */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Genres (ANY match)
            </label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(ALL_GENRES)
                .sort((a, b) => a[1].localeCompare(b[1]))
                .map(([id, name]) => (
                  <button
                    key={id}
                    onClick={() => handleGenreToggle(parseInt(id, 10))}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      selectedGenres.includes(parseInt(id, 10))
                        ? 'bg-teal-500/20 text-teal-400 border border-teal-500/50'
                        : 'bg-[#0F1419] text-slate-400 border border-white/[0.12] hover:border-white/[0.2]'
                    }`}
                  >
                    {name}
                  </button>
                ))}
            </div>
            {selectedGenres.length > 0 && (
              <button
                onClick={() => setSelectedGenres([])}
                className="mt-2 text-xs text-slate-400 hover:text-white"
              >
                Clear genres
              </button>
            )}
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex gap-3 flex-wrap">
            <button
              onClick={fetchStats}
              disabled={statsLoading}
              className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg font-medium hover:bg-teal-600 transition-colors disabled:opacity-50"
            >
              {statsLoading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <BarChart3 size={16} />
              )}
              Get Stats
            </button>

            <button
              onClick={copyFiltersToClipboard}
              className="flex items-center gap-2 px-4 py-2 bg-[#0F1419] text-slate-300 border border-white/[0.12] rounded-lg font-medium hover:border-white/[0.2] transition-colors"
            >
              {copiedFilters ? <CheckCircle2 size={16} /> : <Copy size={16} />}
              {copiedFilters ? 'Copied!' : 'Copy Filters'}
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-400">
            {error}
          </div>
        )}

        {/* Stats Results */}
        {statsResult && (
          <div className="bg-[#1A1F26] border border-white/[0.08] rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Cohort Stats</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Movies Stats */}
              {mediaTypes.includes('movie') && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-teal-400">Movies</h4>
                  <div className="bg-[#0F1419] rounded-lg p-4 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Total</span>
                      <span className="text-white font-medium">{statsResult.stats.movies_total.toLocaleString()}</span>
                    </div>
                    <div className="border-t border-white/[0.08] pt-2 mt-2">
                      <div className="text-xs text-slate-500 mb-2">AI Facets</div>
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div>
                          <div className="text-green-400">{statsResult.stats.movies_facets_tagged.toLocaleString()}</div>
                          <div className="text-xs text-slate-500">Tagged</div>
                        </div>
                        <div>
                          <div className="text-orange-400">{statsResult.stats.movies_facets_missing.toLocaleString()}</div>
                          <div className="text-xs text-slate-500">Missing</div>
                        </div>
                        <div>
                          <div className="text-slate-400">{statsResult.stats.movies_facets_unknown.toLocaleString()}</div>
                          <div className="text-xs text-slate-500">Unknown</div>
                        </div>
                      </div>
                    </div>
                    <div className="border-t border-white/[0.08] pt-2 mt-2">
                      <div className="text-xs text-slate-500 mb-2">Genre Overlay</div>
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div>
                          <div className="text-green-400">{statsResult.stats.movies_overlay_tagged.toLocaleString()}</div>
                          <div className="text-xs text-slate-500">Tagged</div>
                        </div>
                        <div>
                          <div className="text-orange-400">{statsResult.stats.movies_overlay_missing.toLocaleString()}</div>
                          <div className="text-xs text-slate-500">Missing</div>
                        </div>
                        <div>
                          <div className="text-slate-400">{statsResult.stats.movies_overlay_unknown.toLocaleString()}</div>
                          <div className="text-xs text-slate-500">Unknown</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* TV Stats */}
              {mediaTypes.includes('tv') && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-purple-400">TV Shows</h4>
                  <div className="bg-[#0F1419] rounded-lg p-4 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Total</span>
                      <span className="text-white font-medium">{statsResult.stats.tv_total.toLocaleString()}</span>
                    </div>
                    <div className="border-t border-white/[0.08] pt-2 mt-2">
                      <div className="text-xs text-slate-500 mb-2">AI Facets</div>
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div>
                          <div className="text-green-400">{statsResult.stats.tv_facets_tagged.toLocaleString()}</div>
                          <div className="text-xs text-slate-500">Tagged</div>
                        </div>
                        <div>
                          <div className="text-orange-400">{statsResult.stats.tv_facets_missing.toLocaleString()}</div>
                          <div className="text-xs text-slate-500">Missing</div>
                        </div>
                        <div>
                          <div className="text-slate-400">{statsResult.stats.tv_facets_unknown.toLocaleString()}</div>
                          <div className="text-xs text-slate-500">Unknown</div>
                        </div>
                      </div>
                    </div>
                    <div className="border-t border-white/[0.08] pt-2 mt-2">
                      <div className="text-xs text-slate-500 mb-2">Genre Overlay</div>
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div>
                          <div className="text-green-400">{statsResult.stats.tv_overlay_tagged.toLocaleString()}</div>
                          <div className="text-xs text-slate-500">Tagged</div>
                        </div>
                        <div>
                          <div className="text-orange-400">{statsResult.stats.tv_overlay_missing.toLocaleString()}</div>
                          <div className="text-xs text-slate-500">Missing</div>
                        </div>
                        <div>
                          <div className="text-slate-400">{statsResult.stats.tv_overlay_unknown.toLocaleString()}</div>
                          <div className="text-xs text-slate-500">Unknown</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Handoff Button */}
            <div className="mt-6 pt-4 border-t border-white/[0.08]">
              <button
                onClick={handoffToAiTagging}
                className="flex items-center gap-2 px-4 py-2 bg-orange-500/20 text-orange-400 border border-orange-500/50 rounded-lg font-medium hover:bg-orange-500/30 transition-colors"
              >
                <ArrowRight size={16} />
                Run AI Tagging with These Filters
              </button>
            </div>
          </div>
        )}

        {/* Preview Section */}
        <div className="bg-[#1A1F26] border border-white/[0.08] rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Preview Cohort</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Missing Token Selector */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Missing Token
              </label>
              <select
                value={missingToken}
                onChange={(e) => setMissingToken(e.target.value as typeof missingToken)}
                className="w-full px-3 py-2 bg-[#0F1419] border border-white/[0.12] rounded-lg text-white focus:outline-none focus:border-teal-500"
              >
                <option value="missing_ai_facets_v1">missing_ai_facets_v1</option>
                <option value="missing_ai_genre_overlay_v1">missing_ai_genre_overlay_v1</option>
              </select>
            </div>

            {/* Cursor Input */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Cursor (for pagination)
              </label>
              <textarea
                value={cursorJson}
                onChange={(e) => setCursorJson(e.target.value)}
                placeholder='{"imdb_votes": 12345, "tmdb_id": 67890}'
                rows={2}
                className="w-full px-3 py-2 bg-[#0F1419] border border-white/[0.12] rounded-lg text-white text-sm font-mono focus:outline-none focus:border-teal-500"
              />
            </div>
          </div>

          <button
            onClick={fetchPreview}
            disabled={previewLoading}
            className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg font-medium hover:bg-purple-600 transition-colors disabled:opacity-50"
          >
            {previewLoading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Eye size={16} />
            )}
            Preview Items
          </button>

          {/* Preview Results */}
          {previewResult && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-slate-400">
                  Showing {previewResult.count} items
                </span>
                {previewResult.next_cursor && (
                  <button
                    onClick={useCursor}
                    className="text-sm text-teal-400 hover:text-teal-300"
                  >
                    Use Next Cursor
                  </button>
                )}
              </div>

              <div className="bg-[#0F1419] rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/[0.08]">
                      <th className="px-4 py-2 text-left text-slate-400 font-medium">Type</th>
                      <th className="px-4 py-2 text-left text-slate-400 font-medium">ID</th>
                      <th className="px-4 py-2 text-left text-slate-400 font-medium">Title</th>
                      <th className="px-4 py-2 text-left text-slate-400 font-medium">Year</th>
                      <th className="px-4 py-2 text-left text-slate-400 font-medium">Votes</th>
                      <th className="px-4 py-2 text-left text-slate-400 font-medium">Lang</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewResult.items.map((item, idx) => (
                      <tr key={`${item.media_type}-${item.tmdb_id}`} className={idx % 2 === 0 ? '' : 'bg-white/[0.02]'}>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            item.media_type === 'movie'
                              ? 'bg-teal-500/20 text-teal-400'
                              : 'bg-purple-500/20 text-purple-400'
                          }`}>
                            {item.media_type}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-slate-400 font-mono">{item.tmdb_id}</td>
                        <td className="px-4 py-2 text-white">{item.title}</td>
                        <td className="px-4 py-2 text-slate-400">{item.year || '-'}</td>
                        <td className="px-4 py-2 text-slate-400">{item.imdb_votes?.toLocaleString()}</td>
                        <td className="px-4 py-2 text-slate-400">{item.original_language || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {previewResult.next_cursor && (
                <div className="mt-3 p-3 bg-[#0F1419] rounded-lg">
                  <div className="text-xs text-slate-400 mb-1">Next Cursor:</div>
                  <code className="text-xs text-teal-400 font-mono">
                    {JSON.stringify(previewResult.next_cursor)}
                  </code>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
